// ===== BEGIN FILE: src/components/report-v2/sections/PlanetFactorsSection.tsx =====
/**
 * Planet Factors section — transposed table
 * Rows = astrological factors; Columns = 9 planets
 * Groups: Placement · Shadbala Strength · Shadbala Components · Ishta-Kashta · Blended Strength
 */

import React from 'react';
import { Box, Typography } from '@mui/joy';
import { ReportSection } from '../ReportSection';
import type { ReportSectionProps } from '../types';

// ── Constants ──────────────────────────────────────────────────────────────────

const PLANET_ORDER = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];

const PLANET_SYMBOLS: Record<string, string> = {
  Sun: '☉', Moon: '☽', Mars: '♂', Mercury: '☿',
  Jupiter: '♃', Venus: '♀', Saturn: '♄', Rahu: '☊', Ketu: '☋',
};

const PLANET_SHORT: Record<string, string> = {
  Sun: 'Sun', Moon: 'Moon', Mars: 'Mars', Mercury: 'Mer',
  Jupiter: 'Jup', Venus: 'Ven', Saturn: 'Sat', Rahu: 'Rahu', Ketu: 'Ketu',
};

// ── Colour helpers ────────────────────────────────────────────────────────────

/** Shadbala total % threshold colours */
function shadbalaColor(pct: number | undefined): string {
  if (pct == null) return '#6B7280';
  if (pct >= 70) return '#15803D';
  if (pct >= 50) return '#CA8A04';
  return '#DC2626';
}

/** Virupa ratio colour */
function ratioColor(ratio: number | undefined): string {
  if (ratio == null) return '#6B7280';
  if (ratio >= 1.0) return '#15803D';
  if (ratio >= 0.7) return '#CA8A04';
  return '#DC2626';
}

/** Shadbala component colour (same as total but softer thresholds) */
function componentColor(pct: number | undefined): string {
  if (pct == null) return '#6B7280';
  if (pct >= 65) return '#15803D';
  if (pct >= 40) return '#CA8A04';
  return '#DC2626';
}

/** Ishta vs Kashta colour  − for net (ishta − kashta) */
function netIKColor(net: number | undefined): string {
  if (net == null) return '#6B7280';
  if (net > 5) return '#15803D';
  if (net >= -5) return '#CA8A04';
  return '#DC2626';
}

/** Blended strength colour */
function blendedColor(v: number | undefined): string {
  if (v == null) return '#6B7280';
  if (v >= 20) return '#15803D';
  if (v >= 5) return '#4D7C0F';
  if (v >= -5) return '#92400E';
  return '#DC2626';
}

/** Dignity display config */
function dignityStyle(dignity: string): { label: string; color: string; bg: string } {
  switch (dignity) {
    case 'Exalted':      return { label: 'Exalt', color: '#14532D', bg: '#DCFCE7' };
    case 'Moolatrikona': return { label: 'MT',    color: '#1E40AF', bg: '#DBEAFE' };
    case 'Own Sign':     return { label: 'Own',   color: '#1E40AF', bg: '#EFF6FF' };
    case 'Friend':       return { label: 'Frd',   color: '#3730A3', bg: '#EDE9FE' };
    case 'Neutral':      return { label: 'Neu',   color: '#374151', bg: '#F3F4F6' };
    case 'Enemy':        return { label: 'Enm',   color: '#92400E', bg: '#FEF3C7' };
    case 'Debilitated':  return { label: 'Deb',   color: '#7F1D1D', bg: '#FEE2E2' };
    default:             return { label: dignity.slice(0, 4), color: '#374151', bg: '#F3F4F6' };
  }
}

// ── Cell helpers ──────────────────────────────────────────────────────────────

const naCell = (
  <td style={{ textAlign: 'center', color: '#9CA3AF', fontSize: 'inherit' }}>—</td>
);

function numCell(
  value: number | undefined,
  color: string,
  suffix = '',
): React.ReactElement {
  if (value == null) return naCell;
  return (
    <td style={{ textAlign: 'center', color, fontWeight: 600, fontSize: 'inherit' }}>
      {value}{suffix}
    </td>
  );
}

// ── Shared table styles ───────────────────────────────────────────────────────

const TABLE_STYLE: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.72rem',
  fontFamily: 'inherit',
};

const HEADER_ROW_STYLE: React.CSSProperties = {
  height: 28,
};

const DATA_ROW_STYLE: React.CSSProperties = {
  height: 22,
  borderBottom: '1px solid #F3F4F6',
};

const ROW_LABEL_STYLE: React.CSSProperties = {
  textAlign: 'left',
  padding: '2px 8px 2px 4px',
  fontWeight: 500,
  color: '#374151',
  whiteSpace: 'nowrap',
  width: 140,
};

const COL_HEADER_STYLE: React.CSSProperties = {
  textAlign: 'center',
  padding: '4px 2px',
  fontWeight: 700,
  fontSize: '0.72rem',
  color: '#1F2937',
  borderBottom: '2px solid #E5E7EB',
};

// ── Group header row ──────────────────────────────────────────────────────────

function GroupHeaderRow({
  label,
  colspan,
  bg,
  color,
}: {
  label: string;
  colspan: number;
  bg: string;
  color: string;
}) {
  return (
    <tr style={HEADER_ROW_STYLE}>
      <td
        colSpan={colspan}
        style={{
          background: bg,
          color,
          fontWeight: 700,
          fontSize: '0.7rem',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          padding: '4px 6px',
          borderLeft: `4px solid ${color}`,
        }}
      >
        {label}
      </td>
    </tr>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export const PlanetFactorsSection: React.FC<ReportSectionProps> = ({ data, onNavigate }) => {
  const rows = data.planetFactorData;

  if (!rows || rows.length === 0) {
    return (
      <ReportSection
        title="Net Planetary Impact"
        icon="📊"
        pageBreakBefore
        navigateTab="calculator"
        navigateLabel="View Shadbala →"
        onNavigate={onNavigate ? () => onNavigate('calculator') : undefined}
      >
        <Typography level="body-sm" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
          Planet factor data is not available. Ensure the chart is fully computed with Shadbala enabled.
        </Typography>
      </ReportSection>
    );
  }

  // Functional nature map: planet name → benefic/malefic/neutral/yoga_karaka
  const funcNatureMap: Record<string, string> = {};
  (data.birthDetails.planetsInHouses ?? []).forEach(({ planets }) =>
    planets.forEach(pl => { funcNatureMap[pl.name] = pl.functionalNature; })
  );

  const funcNatureColor = (nature: string | undefined): string => {
    if (nature === 'benefic' || nature === 'yoga_karaka') return '#15803D';
    if (nature === 'malefic') return '#DC2626';
    return '#6B7280';
  };

  const funcNatureBg = (nature: string | undefined): string => {
    if (nature === 'benefic' || nature === 'yoga_karaka') return '#F0FDF4';
    if (nature === 'malefic') return '#FEF2F2';
    return '#F9FAFB';
  };

  // Build planet column order based on available data
  const orderedPlanets = PLANET_ORDER.map(name => ({
    name,
    row: rows.find(r => r.name === name),
  })).filter(p => p.row != null);

  const numCols = orderedPlanets.length + 1; // +1 for row-label column

  // Check if we have any shadbala data at all
  const hasShadbala = rows.some(r => r.shadbalaTotalPct != null);
  const hasComponents = rows.some(r => r.sthanaBala != null);
  const hasIK = rows.some(r => r.ishta != null);
  const hasBlended = rows.some(r => r.blendedStrength != null);

  // Print-specific cell style override
  const printStyle = `
    @media print {
      .planet-factors-table { font-size: 6.5pt !important; }
      .planet-factors-table td, .planet-factors-table th { padding: 1.5pt 2pt !important; }
    }
  `;

  return (
    <ReportSection
      title="Net Planetary Impact"
      icon="📊"
      pageBreakBefore
      navigateTab="calculator"
      navigateLabel="View Shadbala →"
      onNavigate={onNavigate ? () => onNavigate('calculator') : undefined}
    >
      <style>{printStyle}</style>

      {/* Intro blurb */}
      <Typography
        level="body-sm"
        sx={{ mb: 2, color: 'text.secondary', fontSize: '0.8rem', lineHeight: 1.5 }}
      >
        The table below shows astrological strength factors for all nine grahas. <strong>Shadbala</strong> measures
        six-fold planetary energy, <strong>Ishta-Kashta</strong> shows benefic vs malefic potential,
        and the <strong>Blended Strength</strong> gives a net verdict integrating all factors.
      </Typography>

      {/* ── TABLE ─────────────────────────────────────────────────────── */}
      <Box
        sx={{
          overflowX: 'auto',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 'sm',
          '@media print': { overflowX: 'visible', border: 'none' },
        }}
      >
        <table className="planet-factors-table" style={TABLE_STYLE}>
          {/* Column headers */}
          <thead>
            <tr>
              <th style={{ ...COL_HEADER_STYLE, textAlign: 'left', padding: '4px 8px', background: '#F9FAFB', width: 140 }}>
                Factor
              </th>
              {orderedPlanets.map(({ name }) => {
                const nature = funcNatureMap[name];
                const hdrColor = funcNatureColor(nature);
                const hdrBg = funcNatureBg(nature);
                return (
                  <th key={name} style={{ ...COL_HEADER_STYLE, minWidth: 58, background: hdrBg, color: hdrColor }}>
                    <span style={{ fontSize: '1.05em' }}>{PLANET_SYMBOLS[name]}</span>{' '}
                    {PLANET_SHORT[name]}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {/* ══ GROUP 1: PLACEMENT ══════════════════════════════════════ */}
            <GroupHeaderRow
              label="Placement"
              colspan={numCols}
              bg="#F0FDF4"
              color="#15803D"
            />

            {/* Sign */}
            <tr style={DATA_ROW_STYLE}>
              <td style={ROW_LABEL_STYLE}>Sign (Rāśi)</td>
              {orderedPlanets.map(({ name, row }) => (
                <td key={name} style={{ textAlign: 'center', fontSize: 'inherit', color: '#1F2937' }}>
                  {row!.sign}
                </td>
              ))}
            </tr>

            {/* House */}
            <tr style={{ ...DATA_ROW_STYLE, background: '#FAFAFA' }}>
              <td style={ROW_LABEL_STYLE}>House (Bhāva)</td>
              {orderedPlanets.map(({ name, row }) => (
                <td key={name} style={{ textAlign: 'center', fontSize: 'inherit', fontWeight: 600, color: '#374151' }}>
                  H{row!.house}
                </td>
              ))}
            </tr>

            {/* Dignity */}
            <tr style={DATA_ROW_STYLE}>
              <td style={ROW_LABEL_STYLE}>Dignity</td>
              {orderedPlanets.map(({ name, row }) => {
                const ds = dignityStyle(row!.dignity);
                return (
                  <td key={name} style={{ textAlign: 'center', fontSize: 'inherit' }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '1px 5px',
                      borderRadius: 4,
                      background: ds.bg,
                      color: ds.color,
                      fontSize: '0.68rem',
                      fontWeight: 700,
                    }}>
                      {ds.label}
                    </span>
                  </td>
                );
              })}
            </tr>

            {/* Retrograde / Combust flags */}
            <tr style={{ ...DATA_ROW_STYLE, background: '#FAFAFA' }}>
              <td style={ROW_LABEL_STYLE}>Flags</td>
              {orderedPlanets.map(({ name, row }) => {
                const flags: string[] = [];
                if (row!.isRetrograde) flags.push('℞');
                if (row!.isCombust) flags.push('🔥');
                return (
                  <td key={name} style={{ textAlign: 'center', fontSize: 'inherit', color: '#DC2626' }}>
                    {flags.length > 0 ? flags.join(' ') : <span style={{ color: '#D1D5DB' }}>·</span>}
                  </td>
                );
              })}
            </tr>

            {/* ══ GROUP 2: SHADBALA STRENGTH ══════════════════════════════ */}
            {hasShadbala && (
              <>
                <GroupHeaderRow
                  label="Shadbala Strength"
                  colspan={numCols}
                  bg="#EFF6FF"
                  color="#1D4ED8"
                />

                {/* Total Shadbala % */}
                <tr style={DATA_ROW_STYLE}>
                  <td style={ROW_LABEL_STYLE}>Total Strength %</td>
                  {orderedPlanets.map(({ name, row }) =>
                    numCell(row!.shadbalaTotalPct, shadbalaColor(row!.shadbalaTotalPct), '%')
                  )}
                </tr>

                {/* Virupa Score */}
                <tr style={{ ...DATA_ROW_STYLE, background: '#FAFAFA' }}>
                  <td style={ROW_LABEL_STYLE}>Virupa Score</td>
                  {orderedPlanets.map(({ name, row }) =>
                    row!.virupas != null
                      ? numCell(row!.virupas, ratioColor(row!.virupasRatio))
                      : naCell
                  )}
                </tr>

                {/* Required Virupa */}
                <tr style={DATA_ROW_STYLE}>
                  <td style={ROW_LABEL_STYLE}>Required Virupa</td>
                  {orderedPlanets.map(({ name, row }) =>
                    row!.virupasRequired != null
                      ? numCell(row!.virupasRequired, '#6B7280')
                      : naCell
                  )}
                </tr>

                {/* Strength Ratio */}
                <tr style={{ ...DATA_ROW_STYLE, background: '#FAFAFA' }}>
                  <td style={ROW_LABEL_STYLE}>Ratio (≥1 = OK)</td>
                  {orderedPlanets.map(({ name, row }) => {
                    if (row!.virupasRatio == null) return naCell;
                    const c = ratioColor(row!.virupasRatio);
                    return (
                      <td key={name} style={{ textAlign: 'center', fontWeight: 700, color: c, fontSize: 'inherit' }}>
                        {row!.virupasRatio.toFixed(2)}
                        {row!.virupasRatio >= 1.0
                          ? <span style={{ color: '#15803D', marginLeft: 2 }}>✓</span>
                          : <span style={{ color: '#DC2626', marginLeft: 2 }}>✗</span>
                        }
                      </td>
                    );
                  })}
                </tr>
              </>
            )}

            {/* ══ GROUP 3: SHADBALA COMPONENTS ════════════════════════════ */}
            {hasComponents && (
              <>
                <GroupHeaderRow
                  label="Shadbala Components (% of Required)"
                  colspan={numCols}
                  bg="#F5F3FF"
                  color="#6D28D9"
                />

                {[
                  { key: 'sthanaBala',     label: 'Sthāna Bala (Position)' },
                  { key: 'digBala',        label: 'Dik Bala (Direction)' },
                  { key: 'kalaBala',       label: 'Kāla Bala (Time)' },
                  { key: 'cheshtaBala',    label: 'Cheṣṭa Bala (Motion)' },
                  { key: 'naisargikaBala', label: 'Naisargika Bala (Natural)' },
                  { key: 'drikBala',       label: 'Dṛk Bala (Aspectual)' },
                ].map(({ key, label }, idx) => (
                  <tr key={key} style={{ ...DATA_ROW_STYLE, ...(idx % 2 === 1 ? { background: '#FAFAFA' } : {}) }}>
                    <td style={ROW_LABEL_STYLE}>{label}</td>
                    {orderedPlanets.map(({ name, row }) => {
                      const val = (row as any)[key] as number | undefined;
                      return numCell(val, componentColor(val), '%');
                    })}
                  </tr>
                ))}
              </>
            )}

            {/* ══ GROUP 4: ISHTA-KASHTA PHALA ═════════════════════════════ */}
            {hasIK && (
              <>
                <GroupHeaderRow
                  label="Ishta-Kashta Phala (Benefic vs Malefic Potential)"
                  colspan={numCols}
                  bg="#FFF7ED"
                  color="#C2410C"
                />

                {/* Ishta Phala */}
                <tr style={DATA_ROW_STYLE}>
                  <td style={ROW_LABEL_STYLE}>Ishṭa Phala (0–60)</td>
                  {orderedPlanets.map(({ name, row }) =>
                    row!.ishta != null
                      ? numCell(row!.ishta, '#15803D')
                      : naCell
                  )}
                </tr>

                {/* Kashta Phala */}
                <tr style={{ ...DATA_ROW_STYLE, background: '#FAFAFA' }}>
                  <td style={ROW_LABEL_STYLE}>Kaṣṭa Phala (0–60)</td>
                  {orderedPlanets.map(({ name, row }) =>
                    row!.kashta != null
                      ? numCell(row!.kashta, '#DC2626')
                      : naCell
                  )}
                </tr>

                {/* Net (Ishta - Kashta) */}
                <tr style={DATA_ROW_STYLE}>
                  <td style={ROW_LABEL_STYLE}>Net (Ishṭa − Kaṣṭa)</td>
                  {orderedPlanets.map(({ name, row }) => {
                    if (row!.ishta == null || row!.kashta == null) return naCell;
                    const net = row!.ishta - row!.kashta;
                    return numCell(net, netIKColor(net));
                  })}
                </tr>

                {/* Ishta % */}
                <tr style={{ ...DATA_ROW_STYLE, background: '#FAFAFA' }}>
                  <td style={ROW_LABEL_STYLE}>Ishṭa % (Benefic)</td>
                  {orderedPlanets.map(({ name, row }) =>
                    row!.ishtaPct != null
                      ? numCell(row!.ishtaPct, '#15803D', '%')
                      : naCell
                  )}
                </tr>

                {/* Kashta % */}
                <tr style={DATA_ROW_STYLE}>
                  <td style={ROW_LABEL_STYLE}>Kaṣṭa % (Malefic)</td>
                  {orderedPlanets.map(({ name, row }) =>
                    row!.kashtaPct != null
                      ? numCell(row!.kashtaPct, '#DC2626', '%')
                      : naCell
                  )}
                </tr>
              </>
            )}

            {/* ══ GROUP 5: BLENDED STRENGTH ═══════════════════════════════ */}
            {hasBlended && (
              <>
                <GroupHeaderRow
                  label="Blended Planet Strength (Net Score: −100 to +100)"
                  colspan={numCols}
                  bg="#FDF2F8"
                  color="#86198F"
                />

                <tr style={DATA_ROW_STYLE}>
                  <td style={ROW_LABEL_STYLE}>Net Planet Impact</td>
                  {orderedPlanets.map(({ name, row }) => {
                    const v = row!.blendedStrength;
                    if (v == null) return naCell;
                    const c = blendedColor(v);
                    const bar = Math.abs(Math.min(100, Math.max(-100, v)));
                    return (
                      <td key={name} style={{ textAlign: 'center', fontSize: 'inherit' }}>
                        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: c }}>
                          {v > 0 ? '+' : ''}{v}
                        </div>
                        {/* Mini horizontal bar */}
                        <div style={{ height: 4, width: '80%', margin: '1px auto', background: '#F3F4F6', borderRadius: 2, overflow: 'hidden' }}>
                          <div style={{
                            height: '100%',
                            width: `${bar}%`,
                            background: c,
                            borderRadius: 2,
                            transition: 'width 0.3s',
                          }} />
                        </div>
                      </td>
                    );
                  })}
                </tr>
              </>
            )}
          </tbody>
        </table>
      </Box>

      {/* Legend */}
      <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 2, fontSize: '0.72rem', color: 'text.secondary' }}>
        <span>
          <strong style={{ color: '#15803D' }}>Green</strong> — strong / beneficial
        </span>
        <span>
          <strong style={{ color: '#CA8A04' }}>Amber</strong> — moderate / mixed
        </span>
        <span>
          <strong style={{ color: '#DC2626' }}>Red</strong> — weak / challenging
        </span>
        <span>℞ — Retrograde &nbsp; 🔥 — Combust</span>
        <span>— = not applicable (Rāhu/Ketu for ratio &amp; Ishṭa-Kaṣṭa)</span>
      </Box>

      {/* Interpretation note */}
      <Box sx={{
        mt: 2, p: 1.5,
        background: '#FFF7ED',
        borderLeft: '3px solid #F59E0B',
        borderRadius: '0 4px 4px 0',
        fontSize: '0.75rem',
        color: '#78350F',
        lineHeight: 1.6,
        '@media print': { fontSize: '7pt' },
      }}>
        <strong>How to read:</strong> Shadbala &gt;70% indicates a strongly placed planet; ratio ≥1.0 means the
        planet meets minimum classical strength. Ishṭa Phala (benefic blessings) vs Kaṣṭa Phala (malefic pressures)
        show the planet's overall tendency. Blended Strength combines all factors — positive values favour the
        planet's life-area themes, negative values suggest more challenges.
      </Box>
    </ReportSection>
  );
};
